import { RowActions } from "@/components/common/RowActions";
import { unitLabel } from "@/components/common/UnitPicker";
import type { Provision } from "@/features/provisions/types";
import { formatQuantity } from "@/lib/format";

type ProvisionListProps = {
  provisions: Provision[];
  onEdit: (provision: Provision) => void;
  onDelete: (provision: Provision) => void;
};

/**
 * A table on a wide screen, stacked cards on a phone: the same data, without a
 * horizontal scroll behind a counter.
 */
export function ProvisionList({ provisions, onEdit, onDelete }: ProvisionListProps) {
  return (
    <>
      <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
        <table className="w-full text-left">
          <thead className="bg-muted/60">
            <tr className="text-[13px] font-semibold text-muted-foreground">
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3 w-28">Unité</th>
              <th className="px-4 py-3 w-44">Se compte par</th>
              <th className="px-4 py-3 w-24 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {provisions.map((provision) => (
              <tr key={provision.id} className="border-t border-muted">
                <td className="px-4 py-3.5">
                  <p className="font-medium">{provision.name}</p>
                  {provision.description ? (
                    <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{provision.description}</p>
                  ) : null}
                </td>
                <td className="px-4 py-3.5 text-[15px]">{unitLabel(provision.measurementUnit)}</td>
                <td className="px-4 py-3.5 text-[15px] tabular-nums">
                  {formatQuantity(provision.measurementPrecision, unitLabel(provision.measurementUnit))}
                </td>
                <td className="px-2 py-3.5">
                  <div className="flex justify-end">
                    <RowActions onEdit={() => onEdit(provision)} onDelete={() => onDelete(provision)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="flex flex-col gap-3 md:hidden">
        {provisions.map((provision) => (
          <li key={provision.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="min-w-0 flex-1 font-semibold">{provision.name}</p>
              <RowActions onEdit={() => onEdit(provision)} onDelete={() => onDelete(provision)} />
            </div>
            {provision.description ? (
              <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{provision.description}</p>
            ) : null}
            <p className="mt-3 text-sm text-muted-foreground">
              Se compte par{" "}
              <span className="font-medium text-foreground tabular-nums">
                {formatQuantity(provision.measurementPrecision, unitLabel(provision.measurementUnit))}
              </span>
            </p>
          </li>
        ))}
      </ul>
    </>
  );
}
